/**
 * Marketing route tree, composable under any parent path.
 *
 * The Vera Shell mounts it at `/marketing/*` via `marketingRoutes('/marketing')`.
 * Mirrors the Sales pattern: a base-path context for absolute `<Link>`s, a
 * ProtectedRoute gate on the `Marketing` role prefix, and its own AppShell.
 */

import { lazy, Suspense, createContext, useContext } from 'react';
import { Navigate, Outlet, type RouteObject } from 'react-router-dom';
import { Spinner } from '@fluentui/react-components';
import { ProtectedRoute } from '@shared/auth';
import { AppShell } from '@widgets/app-shell';
import { ErrorBoundary } from '@shared/ui';
import { ArrowSyncRegular } from '@fluentui/react-icons';
import type { NavItem } from '@widgets/app-shell';
import { veracityColors } from '@shared/ui';

const BrazeSyncPage = lazy(() =>
  import('../../pages/sync-status').then((m) => ({ default: m.BrazeSyncPage })),
);

function LazyPage({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<Spinner style={{ padding: '2rem' }} label="Loading…" />}>{children}</Suspense>;
}

const MarketingBasePathContext = createContext<string>('');

function buildNavItems(basePath: string): NavItem[] {
  const p = (suffix: string) => `${basePath}${suffix}` || '/';
  return [{ path: p('/'), label: 'Braze Sync', icon: ArrowSyncRegular }];
}

export function MarketingRootLayout() {
  const basePath = useContext(MarketingBasePathContext);
  const navItems = buildNavItems(basePath);
  return (
    <ErrorBoundary>
      <AppShell
        appId="marketing"
        appTitle="Vera Marketing"
        navItems={navItems}
        headerColor={veracityColors.horizonBlue}
      >
        <Outlet />
      </AppShell>
    </ErrorBoundary>
  );
}

export function MarketingProtectedLayout() {
  return (
    <ProtectedRoute appRolePrefix="Marketing">
      <MarketingRootLayout />
    </ProtectedRoute>
  );
}

export function marketingRoutes(basePath: string = ''): RouteObject[] {
  return [
    {
      element: (
        <MarketingBasePathContext.Provider value={basePath}>
          <Outlet />
        </MarketingBasePathContext.Provider>
      ),
      children: [
        {
          element: <MarketingProtectedLayout />,
          children: [{ index: true, element: <LazyPage><BrazeSyncPage /></LazyPage> }],
        },
        { path: '*', element: <Navigate to={basePath || '/'} replace /> },
      ],
    },
  ];
}
